/* eslint-disable camelcase */
import { ButtonLinkProps, ButtonProps } from 'component-library';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePlotGroupDefault, useSetPlotGroup } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { ButtonWidget } from '@/components/widgets/a/Button/Button';
import { ButtonLinkWidget } from '@/components/widgets/a/Button/ButtonLink';
import { RecommendationCard } from '@/components/widgets/o/RecommendationCard/types';
import { usePathSet } from '@/hooks/usePathSet';
import { Logger } from '@/lib/logs/logger';
import { buildPath } from '@/utils/buildPath';

type ActionButtonProps = {
  text?: string;
  i18nPath?: string;
} & ButtonProps;

type RecommendationActionButtonProps = {
  recommendationCard: RecommendationCard;
} & ActionButtonProps;

export const RecommendationActionButton: React.FC<RecommendationActionButtonProps> = ({
  recommendationCard,
  ...props
}) => {
  const { t } = useTranslation();
  const { next, affected_assets } = recommendationCard;

  const { addPlotGroup } = useSetPlotGroup();

  const defaultPlotGroup = usePlotGroupDefault('default');

  const handleOnClick = () => {
    const plotGroupId = next?.filter_params?.plot_group_id;

    if (plotGroupId) {
      let plotGroup: typeof defaultPlotGroup = {
        ...defaultPlotGroup,
        id: plotGroupId,
        name: recommendationCard?.header?.text || t('global.widget.recommendationCard.recommendationCard'),
      };

      if (next.sort_orders && next.sort_orders.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { field, direction } = next.sort_orders[0]!;
        plotGroup = {
          ...plotGroup,
          sort: {
            ...plotGroup.sort,
            [field]: direction as 'asc' | 'desc',
          },
        };
      }

      addPlotGroup(plotGroup, true);
    }
  };

  if (!next || (typeof next === 'object' && Object.keys(next).length === 0)) {
    return null;
  }

  const label = affected_assets
    ? t('global.widget.recommendationCard.button.actionLabel.affectedAssets', {
        affected_assets,
      })
    : t('global.widget.recommendationCard.button.actionLabel.fallback');

  if (next.page) {
    return <NavigateToPageButton next={next} text={label} onClick={handleOnClick} {...props} />;
  }

  return <ButtonWidget text={label} onClick={handleOnClick} data-testid='recommendation-action-button' {...props} />;
};

const NavigateToPageButton: React.FC<Pick<RecommendationCard, 'next'> & ActionButtonProps> = ({ next, ...props }) => {
  const to = useGetToPage({ page: next?.page });

  if (!to) {
    return null;
  }

  return <ButtonLinkWidget {...(props as ButtonLinkProps)} to={to} data-testid='navigate-to-page-button' />;
};

const useGetToPage = ({ page }: Pick<NonNullable<RecommendationCard['next']>, 'page'>): string | null => {
  const paths = usePathSet();

  if (!page) {
    return null;
  }

  const backendKnownPages: Record<string, string> = {
    plot_list: paths.plots,
    reports: paths.reports,
  };

  const targetPath = backendKnownPages[`${page}`];

  if (!targetPath) {
    Logger.error(`RecommendationActionButton: backend received page "${page}" is not defined.`);
    throw new Error(`RecommendationActionButton: backend received page "${page}" is not defined.`);
  }

  return buildPath(targetPath);
};
